import { AlertTriangle, FileText } from 'lucide-react';
import { useState } from 'react';
import { useAuth } from '@/auth/AuthContext';
import { RichTextField } from '@/components/Admin/RichTextField';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { updateTermsSettings } from '@/data/settingsRepo';
import { useTermsSettings } from '@/hooks/useTermsSettings';
import { termsSettingsEmpty, type TermsSettingsType } from '@/types/types_settings';
import { formatDate } from '@/util/util_format';

/**
 * The partner agreement: the NDA and the terms of service a manager reads and
 * accepts during registration.
 *
 * Editable here rather than compiled in because the wording is the business's,
 * not engineering's — a clause that turns out to be wrong should be correctable
 * by the person who noticed, in the afternoon, without a release. This is the
 * only copy: nothing is compiled in behind it, so what is typed here is what
 * partners accept, and an empty box means an empty agreement.
 *
 * Two blocks because the step shows two, ruled off from each other. Each is a
 * heading and a body, and the body is rich text: numbered clauses want bold
 * openers and the prohibited-activities clause wants a list.
 */
export function TermsSettingsCard() {
  const { settings, loading } = useTermsSettings();

  return (
    <section className='tw:rounded-lg tw:border tw:bg-card tw:p-6'>
      <div className='tw:flex tw:items-center tw:gap-3'>
        <FileText className='tw:size-5 tw:text-primary' />
        <div>
          <p className='tw:font-medium'>Partner terms &amp; agreement</p>
          <p className='tw:text-sm tw:text-muted-foreground'>
            Shown to a partner manager at step 2 of registration, and to anyone who has not accepted
            yet. Public: anyone with the link reads it before they have an account. Saving changes
            the wording immediately; partners who have already accepted are not asked again.
          </p>
        </div>
      </div>

      {termsSettingsEmpty(settings) && !loading && (
        <p className='tw:mt-5 tw:flex tw:items-start tw:gap-2 tw:rounded-md tw:border tw:border-ed-red/40 tw:bg-ed-red/5 tw:px-4 tw:py-3 tw:text-sm'>
          <AlertTriangle className='tw:mt-0.5 tw:size-4 tw:shrink-0 tw:text-ed-red' />
          <span>
            No agreement is set. Registering partners are told the terms are unavailable and asked to
            contact us — there is no older wording to fall back on.
          </span>
        </p>
      )}

      {loading
        ? <p className='tw:mt-6 tw:text-sm tw:text-muted-foreground'>Loading…</p>
        // Mounted only once the stored wording is in, for the reason the
        // invoicing form is: the fields initialise from it and are then the
        // typist's alone, rather than being overwritten mid-sentence by the
        // subscription.
        : <TermsSettingsForm initial={settings} />}
    </section>
  );
}

function TermsSettingsForm({ initial }: { initial: TermsSettingsType }) {
  const { user } = useAuth();
  const [draft, setDraft] = useState<TermsSettingsType>(initial);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function set<K extends keyof TermsSettingsType>(key: K, value: TermsSettingsType[K]) {
    setDraft((current) => ({ ...current, [key]: value }));
    setSaved(false);
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      await updateTermsSettings(draft, user?.uid);
      setSaved(true);
    } catch (e) {
      console.error('Failed to save terms settings', e);
      setError('Could not save that. Check your connection and try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <div className='tw:mt-6 tw:space-y-8'>
        <TermsSection
          idPrefix='terms-nda'
          legend='Non-disclosure agreement'
          heading={draft.ndaHeading}
          onHeadingChange={(v) => set('ndaHeading', v)}
          body={draft.ndaBody}
          onBodyChange={(v) => set('ndaBody', v)}
        />
        <TermsSection
          idPrefix='terms-service'
          legend='Terms of service'
          heading={draft.serviceHeading}
          onHeadingChange={(v) => set('serviceHeading', v)}
          body={draft.serviceBody}
          onBodyChange={(v) => set('serviceBody', v)}
        />
      </div>

      <div className='tw:mt-6 tw:flex tw:flex-wrap tw:items-center tw:gap-4'>
        <Button onClick={() => void handleSave()} disabled={saving}>
          {saving ? 'Saving…' : 'Save terms'}
        </Button>
        {saved && <span className='tw:text-sm tw:text-muted-foreground'>Saved.</span>}
        {initial.updatedAt && !saved && (
          <span className='tw:text-xs tw:text-muted-foreground'>
            Last changed {formatDate(initial.updatedAt)}.
          </span>
        )}
      </div>

      {error && <p className='tw:mt-3 tw:text-sm tw:text-ed-red'>{error}</p>}
    </>
  );
}

/** One heading-and-body block. Local to this card, as there are exactly two. */
function TermsSection({
  idPrefix,
  legend,
  heading,
  onHeadingChange,
  body,
  onBodyChange,
}: {
  idPrefix: string;
  legend: string;
  heading: string;
  onHeadingChange: (value: string) => void;
  body: string;
  onBodyChange: (value: string) => void;
}) {
  return (
    <fieldset className='tw:space-y-4'>
      <legend className='tw:text-xs tw:font-medium tw:tracking-wider tw:uppercase tw:text-muted-foreground'>
        {legend}
      </legend>

      <div className='tw:space-y-2'>
        <Label htmlFor={`${idPrefix}-heading`}>Heading</Label>
        <Input
          id={`${idPrefix}-heading`}
          value={heading}
          onChange={(e) => onHeadingChange(e.target.value)}
        />
      </div>

      <div className='tw:space-y-2'>
        {/* No htmlFor: the editor is a contenteditable surface, not a labelable control. */}
        <Label>Text</Label>
        <RichTextField
          value={body}
          onChange={onBodyChange}
          placeholder='The wording partners accept…'
        />
        <p className='tw:text-xs tw:text-muted-foreground'>
          Leave blank to drop this block from the agreement entirely.
        </p>
      </div>
    </fieldset>
  );
}
